import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
    LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { TrendingUp, Briefcase, Users, Trophy, Phone, Calendar } from "lucide-react";

const COLORS = ["#002FA7", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#ec4899", "#84cc16"];

const RANGE_OPTIONS = [
    { v: 7, l: "7 days" },
    { v: 14, l: "14 days" },
    { v: 30, l: "30 days" },
    { v: 90, l: "90 days" },
];

const EMPLOYMENT_TYPES = ["All", "Full-time", "Part-time", "Contract", "Internship", "Freelance"];

export default function AnalyticsDashboard() {
    const [days, setDays] = useState(30);
    const [employmentType, setEmploymentType] = useState("All");
    const [clientId, setClientId] = useState("");
    const [recruiterId, setRecruiterId] = useState("");
    const [overview, setOverview] = useState(null);
    const [series, setSeries] = useState([]);
    const [recruiters, setRecruiters] = useState([]);
    const [clients, setClients] = useState([]);
    const [distribution, setDistribution] = useState({});
    const [recruitersData, setRecruitersData] = useState([]);
    const [clientsData, setClientsData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/company/recruiters").then((r) => setRecruiters(r.data || [])).catch(() => {});
        api.get("/clients").then((r) => setClients(r.data?.items || [])).catch(() => {});
    }, []);

    useEffect(() => {
        const fetchAll = async () => {
            setLoading(true);
            try {
                const params = { days };
                if (employmentType !== "All") params.employment_type = employmentType;
                if (clientId) params.client_id = clientId;
                if (recruiterId) params.recruiter_id = recruiterId;
                const [ov, ts, dist, rec, cli] = await Promise.all([
                    api.get("/analytics/overview", { params: { days } }),
                    api.get("/analytics/timeseries", { params }),
                    api.get("/analytics/distribution", { params: { days } }),
                    api.get("/analytics/by-recruiter", { params: { days } }),
                    api.get("/analytics/by-client", { params: { days } }),
                ]);
                setOverview(ov.data);
                setSeries(ts.data?.series || []);
                setDistribution(dist.data || {});
                setRecruitersData(rec.data?.recruiters || []);
                setClientsData(cli.data?.clients || []);
            } catch (err) { toast.error(formatApiError(err)); }
            finally { setLoading(false); }
        };
        fetchAll();
    }, [days, employmentType, clientId, recruiterId]);

    return (
        <><div className="max-w-screen-2xl mx-auto p-3 sm:p-5 lg:p-6 space-y-5 sm:space-y-6">
                <div className="flex items-end justify-between gap-3 sm:gap-4 flex-wrap">
                    <div className="min-w-0">
                        <div className="text-xs uppercase tracking-widest font-mono text-ikb">Analytics</div>
                        <h1 className="font-display font-black text-2xl sm:text-4xl mt-2">Hiring command center</h1>
                        <p className="text-xs sm:text-sm text-muted-foreground mt-2">Real-time view of pipeline velocity, recruiter productivity, and client performance.</p>
                    </div>
                    <div className="flex flex-wrap gap-2 w-full sm:w-auto" data-testid="analytics-filters">
                        <Picker label="Range" value={days} onChange={(v) => setDays(parseInt(v))} options={RANGE_OPTIONS.map(o => [o.v, o.l])} testId="filter-range" />
                        <Picker label="Employment" value={employmentType} onChange={setEmploymentType} options={EMPLOYMENT_TYPES.map(x => [x, x])} testId="filter-employment" />
                        <Picker label="Client" value={clientId} onChange={setClientId} options={[["", "All clients"], ...clients.map(c => [c.id, c.name])]} testId="filter-client" />
                        <Picker label="Recruiter" value={recruiterId} onChange={setRecruiterId} options={[["", "All recruiters"], ...recruiters.map(r => [r.id, r.name])]} testId="filter-recruiter" />
                    </div>
                </div>

                {/* KPI tiles */}
                {overview && (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-border" data-testid="analytics-kpis">
                        <Kpi icon={Briefcase} label="Active jobs" value={overview.active_jobs} />
                        <Kpi icon={Users} label="Candidates" value={overview.total_candidates} />
                        <Kpi icon={TrendingUp} label={`Applications · ${days}d`} value={overview.applications_window} />
                        <Kpi icon={Trophy} label={`Hires · ${days}d`} value={overview.hires_window} />
                        <Kpi icon={Phone} label={`Follow-ups · ${days}d`} value={overview.followups_window} />
                    </div>
                )}

                {/* Daily activity line chart */}
                <Card title="Daily activity" sub={`Applications · shortlists · follow-ups · hires · last ${days} days`} testId="chart-timeseries">
                    {series.length === 0 ? <Empty /> : (
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={series}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis dataKey="date" tick={{ fontSize: 11, fontFamily: "monospace" }} />
                                <YAxis tick={{ fontSize: 11, fontFamily: "monospace" }} allowDecimals={false} />
                                <Tooltip />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                <Line type="monotone" dataKey="applications" stroke="#002FA7" strokeWidth={2} dot={false} />
                                <Line type="monotone" dataKey="shortlists" stroke="#10b981" strokeWidth={2} dot={false} />
                                <Line type="monotone" dataKey="followups" stroke="#f59e0b" strokeWidth={2} dot={false} />
                                <Line type="monotone" dataKey="hires" stroke="#ef4444" strokeWidth={2} dot={false} />
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                </Card>

                <div className="grid lg:grid-cols-2 gap-px bg-border">
                    <Card title="Applications by employment type" testId="chart-employment-types">
                        {(distribution.employment_types || []).length === 0 ? <Empty /> : (
                            <ResponsiveContainer width="100%" height={260}>
                                <PieChart>
                                    <Pie data={distribution.employment_types} dataKey="value" nameKey="label" outerRadius={90} label>
                                        {distribution.employment_types.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                                    </Pie>
                                    <Tooltip />
                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                </PieChart>
                            </ResponsiveContainer>
                        )}
                    </Card>
                    <Card title="Jobs by work mode" testId="chart-work-modes">
                        {(distribution.work_modes || []).length === 0 ? <Empty /> : (
                            <ResponsiveContainer width="100%" height={260}>
                                <PieChart>
                                    <Pie data={distribution.work_modes} dataKey="value" nameKey="label" innerRadius={50} outerRadius={90} label>
                                        {distribution.work_modes.map((_, i) => <Cell key={i} fill={COLORS[(i + 2) % COLORS.length]} />)}
                                    </Pie>
                                    <Tooltip />
                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                </PieChart>
                            </ResponsiveContainer>
                        )}
                    </Card>
                    <Card title="Follow-ups by outcome" testId="chart-followup-statuses">
                        {(distribution.followup_statuses || []).length === 0 ? <Empty /> : (
                            <ResponsiveContainer width="100%" height={260}>
                                <BarChart data={distribution.followup_statuses}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                    <XAxis dataKey="label" tick={{ fontSize: 9, fontFamily: "monospace" }} interval={0} angle={-15} textAnchor="end" height={60} />
                                    <YAxis tick={{ fontSize: 11, fontFamily: "monospace" }} allowDecimals={false} />
                                    <Tooltip />
                                    <Bar dataKey="value" fill="#002FA7" />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </Card>
                    <Card title="Job pipeline (status mix)" testId="chart-job-statuses">
                        {(distribution.job_statuses || []).length === 0 ? <Empty /> : (
                            <ResponsiveContainer width="100%" height={260}>
                                <PieChart>
                                    <Pie data={distribution.job_statuses} dataKey="value" nameKey="label" outerRadius={90} label>
                                        {distribution.job_statuses.map((_, i) => <Cell key={i} fill={COLORS[(i + 4) % COLORS.length]} />)}
                                    </Pie>
                                    <Tooltip />
                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                </PieChart>
                            </ResponsiveContainer>
                        )}
                    </Card>
                </div>

                {/* Recruiter performance */}
                <Card title={`Recruiter performance · last ${days} days`} testId="chart-recruiters">
                    {recruitersData.length === 0 ? <Empty /> : (
                        <ResponsiveContainer width="100%" height={Math.max(260, recruitersData.length * 50)}>
                            <BarChart data={recruitersData} layout="vertical" margin={{ left: 30 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis type="number" tick={{ fontSize: 11, fontFamily: "monospace" }} />
                                <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
                                <Tooltip />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                <Bar dataKey="applications_touched" name="Applications touched" fill="#002FA7" />
                                <Bar dataKey="followups" name="Follow-ups" fill="#f59e0b" />
                                <Bar dataKey="interviews" name="Interviews" fill="#10b981" />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </Card>

                <Card title={`Daily active time on portal · last ${days} days`} sub="Active minutes derived from heartbeats" testId="chart-active-minutes">
                    {recruitersData.length === 0 ? <Empty /> : (
                        <ResponsiveContainer width="100%" height={260}>
                            <AreaChart data={recruitersData}>
                                <defs>
                                    <linearGradient id="actMin" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#002FA7" stopOpacity={0.55} />
                                        <stop offset="95%" stopColor="#002FA7" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-10} textAnchor="end" height={50} />
                                <YAxis tick={{ fontSize: 11, fontFamily: "monospace" }} allowDecimals={false} />
                                <Tooltip />
                                <Area type="monotone" dataKey="active_minutes" stroke="#002FA7" fill="url(#actMin)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    )}
                </Card>

                {/* Client performance */}
                {clientsData.length > 0 && (
                    <Card title={`Performance by client · last ${days} days`} testId="chart-clients">
                        <ResponsiveContainer width="100%" height={Math.max(260, clientsData.length * 50)}>
                            <BarChart data={clientsData} layout="vertical" margin={{ left: 30 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis type="number" tick={{ fontSize: 11, fontFamily: "monospace" }} />
                                <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11 }} />
                                <Tooltip />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                <Bar dataKey="open_jobs" name="Open jobs" fill="#002FA7" />
                                <Bar dataKey="applications" name="Applications" fill="#f59e0b" />
                                <Bar dataKey="hires" name="Hires" fill="#10b981" />
                            </BarChart>
                        </ResponsiveContainer>
                    </Card>
                )}

                {loading && <div className="text-xs font-mono text-foreground/60">Refreshing…</div>}
            </div>
        </>);
}

function Kpi({ icon: Icon, label, value }) {
    return (
        <div className="bg-white p-4">
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-foreground/70">
                <Icon className="w-3.5 h-3.5" /> {label}
            </div>
            <div className="text-3xl font-display font-black mt-1">{value ?? "—"}</div>
        </div>
    );
}

function Card({ title, sub, children, testId }) {
    return (
        <div className="bg-white p-4" data-testid={testId}>
            <div>
                <h3 className="font-display font-bold text-sm uppercase tracking-widest">{title}</h3>
                {sub && <p className="text-[11px] text-foreground/70 mt-0.5 font-mono">{sub}</p>}
            </div>
            <div className="mt-3">{children}</div>
        </div>
    );
}

function Empty() {
    return <div className="text-xs text-foreground/60 italic py-12 text-center">No data yet for the selected filters.</div>;
}

function Picker({ label, value, onChange, options, testId }) {
    return (
        <label className="flex items-center gap-2">
            <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/70">{label}</span>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="h-9 px-2 border border-border bg-white text-xs font-mono"
                data-testid={testId}
            >
                {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
        </label>
    );
}
